import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../services/supabaseClient'
import Navbar from '../components/Navbar'
import { useWorkspace } from '../context/WorkspaceContext'
import { useSalesforce } from '../hooks/useSalesforce'
import TeamManagement from '../components/TeamManagement'

export default function SettingsPage() {
  const { user, signOut } = useAuth()
  const { orgId: urlOrgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { setCurrentOrgId } = useWorkspace()
  const { orgs, isSuperAdmin } = useSalesforce()
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [activeTab, setActiveTab] = useState('account')
  const [accountData, setAccountData] = useState<any>(null)
  const [userRole, setUserRole] = useState<string>('')
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false)
  const [resetPasswordMessage, setResetPasswordMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false)
  const [passwordChangeMessage, setPasswordChangeMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  const [deletingAccount, setDeletingAccount] = useState(false)
  const [accountName, setAccountName] = useState('')
  const [updatingAccountName, setUpdatingAccountName] = useState(false)
  const [userProfile, setUserProfile] = useState({
    firstName: '',
    lastName: ''
  })
  const [updatingProfile, setUpdatingProfile] = useState(false)
  const [profileUpdateMessage, setProfileUpdateMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null)
  
  const [formData, setFormData] = useState({
    emailNotifications: true,
    syncFrequency: 'daily',
    autoSync: true,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  })

  // Handle tab from URL query parameter and validate access
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam && ['account', 'sync', 'team'].includes(tabParam)) {
      // Check if user has access to requested tab
      if (tabParam === 'team' && !['Super-Admin', 'Admin'].includes(userRole)) {
        setActiveTab('account')
      } else if (tabParam === 'sync' && userRole === 'Viewer') {
        setActiveTab('account')
      } else {
        setActiveTab(tabParam)
      }
    }
  }, [searchParams, userRole])

  // Handle org context from URL
  useEffect(() => {
    if (urlOrgId) {
      setCurrentOrgId(urlOrgId)
    } else if (orgs.length > 0 && !urlOrgId) {
      // If no org in URL but we have orgs, redirect to the first one
      navigate(`/${orgs[0].id}/settings`, { replace: true })
    }
  }, [urlOrgId, orgs, navigate, setCurrentOrgId])

  // Fetch account data on mount
  useEffect(() => {
    if (!user) return;

    const fetchAccountData = async () => {
      try {
        // Get the current account ID from localStorage
        const currentAccountId = localStorage.getItem('currentAccountId');

        if (!currentAccountId) {
          console.log('No current account ID found');
          return;
        }

        const { data, error } = await supabase
          .from('user_account_roles')
          .select(`
            account_id,
            account:accounts (
              id,
              name,
              subscription_tier,
              subscription_status,
              created_at
            ),
            roles (
              name
            )
          `)
          .eq('user_id', user.id)
          .eq('account_id', currentAccountId)
          .single();

        if (error) throw error;
        console.log('Account data fetched:', data);
        setAccountData({ ...data?.account, account_id: data?.account_id });
        setAccountName(data?.account?.name || '');
        setUserRole(data?.roles?.name || '');
      } catch (err) {
        console.error('Error fetching account data:', err);
      }
    };

    const fetchUserPreferences = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session) return
        
        const response = await fetch('/api/user/preferences', {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        })
        
        if (response.ok) {
          const { preferences } = await response.json()
          setFormData({
            emailNotifications: preferences.email_notifications,
            syncFrequency: preferences.sync_frequency,
            autoSync: preferences.auto_sync,
            timezone: preferences.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
          })
        }
      } catch (err) {
        console.error('Error fetching preferences:', err)
      }
    }

    const fetchUserProfile = async () => {
      try {
        // First check if profile exists
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('user_id', user.id)
          .single()
        
        if (profile && !error) {
          // Parse the full_name if it exists
          if (profile.full_name) {
            const nameParts = profile.full_name.split(' ')
            setUserProfile({
              firstName: nameParts[0] || '',
              lastName: nameParts.slice(1).join(' ') || ''
            })
          }
        } else if (error && error.code === 'PGRST116') {
          // Profile doesn't exist yet, try to get from auth metadata
          const { data: { user: authUser } } = await supabase.auth.getUser()
          if (authUser?.user_metadata) {
            const { first_name, last_name, full_name, name } = authUser.user_metadata
            if (first_name || last_name) {
              setUserProfile({
                firstName: first_name || '',
                lastName: last_name || ''
              })
            } else if (full_name || name) {
              const fullName = full_name || name
              const nameParts = fullName.split(' ')
              setUserProfile({
                firstName: nameParts[0] || '',
                lastName: nameParts.slice(1).join(' ') || ''
              })
            }
          }
        }
      } catch (err) {
        console.error('Error fetching user profile:', err)
      }
    }

    fetchAccountData();
    fetchUserPreferences();
    fetchUserProfile();
  }, [user]);
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    })
  }
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    setSaveSuccess(false)
    
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        alert('Please sign in to save preferences')
        setIsSaving(false)
        return
      }
      
      const response = await fetch('/api/user/preferences', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })
      
      const result = await response.json()
      
      if (response.ok) {
        setSaveSuccess(true)
        setTimeout(() => {
          setSaveSuccess(false)
        }, 3000)
      } else {
        alert('Failed to save settings: ' + (result.error || 'Unknown error'))
      }
    } catch (err) {
      console.error('Error saving preferences:', err)
      alert('Failed to save settings. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }
  
  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }
  
  const handlePasswordReset = async () => {
    if (!user?.email) return
    
    setResetPasswordLoading(true)
    setResetPasswordMessage(null)
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: `${window.location.origin}/update-password`,
      })
      
      if (error) throw error
      
      setResetPasswordMessage({
        type: 'success',
        text: `Password reset email sent to ${user.email}. Please check your inbox.`
      })
    } catch (error: any) {
      console.error('Error sending password reset email:', error)
      setResetPasswordMessage({
        type: 'error',
        text: error.message || 'Failed to send password reset email. Please try again.'
      })
    } finally {
      setResetPasswordLoading(false)
    }
  }
  
  const handleDeleteAccount = async () => {
    setDeletingAccount(true)
    
    try {
      // First, get a preview of what will be deleted
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('No session')
      
      const previewResponse = await fetch('/api/account/delete-preview', {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      })
      
      if (!previewResponse.ok) {
        throw new Error('Failed to get deletion preview')
      }
      
      const preview = await previewResponse.json()
      
      // Build warning message
      let warningMessage = 'This will permanently delete your user account.\n\n'
      
      if (preview.accountsToDelete.length > 0) {
        warningMessage += '⚠️ WARNING: You are the only Super-Admin for the following accounts:\n'
        preview.accountsToDelete.forEach((account: any) => {
          warningMessage += `\n• ${account.name}`
          warningMessage += `\n  - ${account.orgCount} organizations`
          warningMessage += `\n  - ${account.workspaceCount} workspaces`
          warningMessage += `\n  - ${account.flowCount} flows`
          if (account.otherUsersCount > 0) {
            warningMessage += `\n  - ${account.otherUsersCount} other users will lose access!`
          }
        })
        warningMessage += '\n\nThese accounts and ALL their data will be permanently deleted!'
      }
      
      if (preview.accountsToLeave.length > 0) {
        warningMessage += '\n\nYou will be removed from these accounts (they will continue to exist):\n'
        preview.accountsToLeave.forEach((account: any) => {
          warningMessage += `\n• ${account.name} (${account.otherSuperAdminCount} other Super-Admin${account.otherSuperAdminCount !== 1 ? 's' : ''})`
        })
      }
      
      warningMessage += '\n\nThis action cannot be undone. Are you sure?'
      
      const firstConfirm = confirm(warningMessage)
      if (!firstConfirm) {
        setDeletingAccount(false)
        return
      }
      
      if (preview.otherUsersAffected > 0) {
        const secondConfirm = confirm(`⚠️ FINAL WARNING: ${preview.otherUsersAffected} other user${preview.otherUsersAffected !== 1 ? 's' : ''} will lose access to their data!\n\nAre you absolutely sure you want to proceed?`)
        if (!secondConfirm) {
          setDeletingAccount(false)
          return
        }
      }
      
      const finalConfirm = prompt('Please type "DELETE" to confirm account deletion:')
      if (finalConfirm !== 'DELETE') {
        alert('Account deletion cancelled.')
        setDeletingAccount(false)
        return
      }
      
      // Proceed with deletion
      const response = await fetch('/api/account/delete-user', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          confirm: true 
        })
      })
      
      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to delete account')
      }
      
      // Sign out and redirect to login
      await signOut()
      navigate('/login?message=Account+deleted+successfully')
    } catch (error: any) {
      console.error('Error deleting account:', error)
      alert(`Failed to delete account: ${error.message}`)
      setDeletingAccount(false)
    }
  }
  
  const handleAccountNameUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!accountName.trim() || accountName === accountData?.name) {
      return
    }
    
    setUpdatingAccountName(true)
    
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        alert('Please sign in to update account name')
        return
      }
      
      const { error } = await supabase
        .from('accounts')
        .update({ name: accountName.trim() })
        .eq('id', accountData?.account_id)
      
      if (error) throw error
      
      // Update local state
      setAccountData(prev => prev ? { ...prev, name: accountName.trim() } : null)
      alert('Account name updated successfully!')
      
      // Refresh the page to update navbar
      window.location.reload()
    } catch (error: any) {
      console.error('Error updating account name:', error)
      alert('Failed to update account name: ' + error.message)
    } finally {
      setUpdatingAccountName(false)
    }
  }
  
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordChangeMessage(null)
    
    // Validate passwords
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordChangeMessage({
        type: 'error',
        text: 'New passwords do not match'
      })
      return
    }
    
    if (passwordForm.newPassword.length < 6) {
      setPasswordChangeMessage({
        type: 'error',
        text: 'Password must be at least 6 characters long'
      })
      return
    }
    
    setPasswordChangeLoading(true)
    
    try {
      // For logged-in users, we can directly update the password
      const { error } = await supabase.auth.updateUser({
        password: passwordForm.newPassword
      })
      
      if (error) throw error
      
      setPasswordChangeMessage({
        type: 'success',
        text: 'Password updated successfully!'
      })
      
      // Clear form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      })
      
      // Hide form after success
      setTimeout(() => {
        setShowPasswordForm(false)
        setPasswordChangeMessage(null)
      }, 3000)
      
    } catch (error: any) {
      console.error('Error updating password:', error)
      setPasswordChangeMessage({
        type: 'error',
        text: error.message || 'Failed to update password. Please try again.'
      })
    } finally {
      setPasswordChangeLoading(false)
    }
  }
  
  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    setUpdatingProfile(true)
    setProfileUpdateMessage(null)
    
    try {
      if (!user) throw new Error('No user found')
      
      const fullName = `${userProfile.firstName} ${userProfile.lastName}`.trim()
      
      // First, update auth metadata
      const { error: authError } = await supabase.auth.updateUser({
        data: {
          first_name: userProfile.firstName,
          last_name: userProfile.lastName,
          full_name: fullName
        }
      })
      
      if (authError) throw authError
      
      // Then update or insert into user_profiles table
      const { error: profileError } = await supabase
        .from('user_profiles')
        .upsert({
          user_id: user.id,
          email: user.email,
          full_name: fullName,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        })
      
      if (profileError) throw profileError
      
      setProfileUpdateMessage({
        type: 'success',
        text: 'Profile updated successfully!'
      })
      
      // Clear message after 3 seconds
      setTimeout(() => {
        setProfileUpdateMessage(null)
      }, 3000)
      
    } catch (error: any) {
      console.error('Error updating profile:', error)
      setProfileUpdateMessage({
        type: 'error',
        text: error.message || 'Failed to update profile. Please try again.'
      })
    } finally {
      setUpdatingProfile(false)
    }
  }
  
  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar onSignOut={handleSignOut} />
      
      <div className="py-10">
        <header>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-3xl font-bold leading-tight text-gray-900">Account Settings</h1>
          </div>
        </header>
        <main>
          <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <div className="px-4 py-8 sm:px-0">
              {/* Tab Navigation */}
              <div className="bg-white shadow sm:rounded-lg">
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex">
                    <button
                      onClick={() => setActiveTab('account')}
                      className={`${activeTab === 'account' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm`}
                    >
                      Account
                    </button>
                    {userRole !== 'Viewer' && (
                      <button
                        onClick={() => setActiveTab('sync')}
                        className={`${activeTab === 'sync' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm`}
                      >
                        Sync
                      </button>
                    )}
                    {['Super-Admin', 'Admin'].includes(userRole) && (
                      <button
                        onClick={() => setActiveTab('team')}
                        className={`${activeTab === 'team' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm`}
                      >
                        Team
                      </button>
                    )}
                  </nav>
                </div>
              </div>
              
              {/* Tab Content */}
              {activeTab === 'sync' && (
              <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Sync Settings</h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">Configure how FlowDocs syncs with your Salesforce org.</p>
                </div>
                
                {saveSuccess && (
                  <div className="bg-green-50 p-4 mb-6 mx-6">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-green-800">Settings saved successfully</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <form onSubmit={handleSubmit}>
                  <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-6">
                        <fieldset>
                          <legend className="text-base font-medium text-gray-900">Notifications</legend>
                          <div className="mt-4 space-y-4">
                            <div className="flex items-start">
                              <div className="flex items-center h-5">
                                <input
                                  id="emailNotifications"
                                  name="emailNotifications"
                                  type="checkbox"
                                  checked={formData.emailNotifications}
                                  onChange={handleInputChange}
                                  className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                                />
                              </div>
                              <div className="ml-3 text-sm">
                                <label htmlFor="emailNotifications" className="font-medium text-gray-700">Email notifications</label>
                                <p className="text-gray-500">Receive email notifications when flows are changed or updated.</p>
                              </div>
                            </div>
                          </div>
                        </fieldset>
                      </div>
                      
                      <div className="sm:col-span-3">
                        <label htmlFor="syncFrequency" className="block text-sm font-medium text-gray-700">Sync Frequency</label>
                        <select
                          id="syncFrequency"
                          name="syncFrequency"
                          value={formData.syncFrequency}
                          onChange={handleInputChange}
                          className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="manual">Manual only</option>
                        </select>
                        <p className="mt-2 text-sm text-gray-500">How often should we check for flow changes.</p>
                      </div>
                      
                      <div className="sm:col-span-3">
                        <label htmlFor="timezone" className="block text-sm font-medium text-gray-700">Timezone</label>
                        <select
                          id="timezone"
                          name="timezone"
                          value={formData.timezone}
                          onChange={handleInputChange}
                          className="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        >
                          <option value="America/New_York">Eastern Time (ET)</option>
                          <option value="America/Chicago">Central Time (CT)</option>
                          <option value="America/Denver">Mountain Time (MT)</option>
                          <option value="America/Los_Angeles">Pacific Time (PT)</option>
                          <option value="America/Anchorage">Alaska Time (AKT)</option>
                          <option value="Pacific/Honolulu">Hawaii Time (HT)</option>
                          <option value="Etc/UTC">UTC</option>
                        </select>
                        <p className="mt-2 text-sm text-gray-500">Your preferred timezone for dates and times.</p>
                      </div>
                      
                      <div className="sm:col-span-6">
                        <div className="flex items-start">
                          <div className="flex items-center h-5">
                            <input
                              id="autoSync"
                              name="autoSync"
                              type="checkbox"
                              checked={formData.autoSync}
                              onChange={handleInputChange}
                              className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                            />
                          </div>
                          <div className="ml-3 text-sm">
                            <label htmlFor="autoSync" className="font-medium text-gray-700">Auto-sync newly connected orgs</label>
                            <p className="text-gray-500">Automatically sync flow data when you connect a new Salesforce org.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                    <button
                      type="submit"
                      disabled={isSaving}
                      className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                        isSaving ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'
                      } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                    >
                      {isSaving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                </form>
              </div>
              )}
              
              {/* Team Tab */}
              {activeTab === 'team' && (
                <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
                  <div className="px-4 py-5 sm:px-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Team Management</h3>
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">Manage team members and their permissions.</p>
                  </div>
                  <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                    {accountData?.account_id ? (
                      <TeamManagement accountId={accountData.account_id} />
                    ) : (
                      <div className="text-center text-gray-500">Loading team data...</div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Account Tab */}
              {activeTab === 'account' && (
              <>
              {/* User Profile Section */}
              <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">User Profile</h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">Manage your personal information and account preferences.</p>
                </div>
                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                  <form onSubmit={handleProfileUpdate} className="space-y-4">
                    {profileUpdateMessage && (
                      <div className={`rounded-md p-4 ${
                        profileUpdateMessage.type === 'success' 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-red-50 border border-red-200'
                      }`}>
                        <div className="flex">
                          <div className="flex-shrink-0">
                            {profileUpdateMessage.type === 'success' ? (
                              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <div className="ml-3">
                            <p className={`text-sm font-medium ${
                              profileUpdateMessage.type === 'success' ? 'text-green-800' : 'text-red-800'
                            }`}>
                              {profileUpdateMessage.text}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                          First Name
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          id="firstName"
                          value={userProfile.firstName}
                          onChange={(e) => setUserProfile(prev => ({ ...prev, firstName: e.target.value }))}
                          className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          placeholder="John"
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                          Last Name
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          id="lastName"
                          value={userProfile.lastName}
                          onChange={(e) => setUserProfile(prev => ({ ...prev, lastName: e.target.value }))}
                          className="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          placeholder="Doe"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        type="submit"
                        disabled={updatingProfile}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
                      >
                        {updatingProfile ? 'Saving...' : 'Update Profile'}
                      </button>
                    </div>
                    
                    {/* Email Address field */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                        <div className="sm:col-span-4">
                          <label className="block text-sm font-medium text-gray-700">Email Address</label>
                          <div className="mt-1 flex rounded-md shadow-sm">
                            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm">
                              Email
                            </span>
                            <input
                              type="text"
                              value={user?.email || ''}
                              readOnly
                              className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md bg-gray-100 focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300 cursor-not-allowed"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-6">
                        {!showPasswordForm ? (
                          <div>
                            <button
                              type="button"
                              onClick={() => setShowPasswordForm(true)}
                              className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              Change Password
                            </button>
                            
                            <p className="mt-3 text-sm text-gray-500">
                              Or <button
                                type="button"
                                onClick={handlePasswordReset}
                                disabled={resetPasswordLoading}
                                className="font-medium text-blue-600 hover:text-blue-500"
                              >
                                {resetPasswordLoading ? 'sending...' : 'send a password reset email'}
                              </button> instead
                            </p>
                            
                            {resetPasswordMessage && (
                              <div className={`mt-3 text-sm ${
                                resetPasswordMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {resetPasswordMessage.text}
                              </div>
                            )}
                          </div>
                        ) : (
                          <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
                            <div>
                              <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                                New Password
                              </label>
                              <input
                                type="password"
                                id="new-password"
                                value={passwordForm.newPassword}
                                onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                                required
                                minLength={6}
                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="Enter new password"
                              />
                            </div>
                            
                            <div>
                              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                                Confirm New Password
                              </label>
                              <input
                                type="password"
                                id="confirm-password"
                                value={passwordForm.confirmPassword}
                                onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                                required
                                minLength={6}
                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                placeholder="Confirm new password"
                              />
                            </div>
                            
                            {passwordChangeMessage && (
                              <div className={`text-sm ${
                                passwordChangeMessage.type === 'success' ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {passwordChangeMessage.text}
                              </div>
                            )}
                            
                            <div className="flex space-x-3">
                              <button
                                type="submit"
                                disabled={passwordChangeLoading}
                                className={`inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white ${
                                  passwordChangeLoading 
                                    ? 'bg-gray-400 cursor-not-allowed' 
                                    : 'bg-blue-600 hover:bg-blue-700'
                                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                              >
                                {passwordChangeLoading ? 'Updating...' : 'Update Password'}
                              </button>
                              
                              <button
                                type="button"
                                onClick={() => {
                                  setShowPasswordForm(false)
                                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
                                  setPasswordChangeMessage(null)
                                }}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                Cancel
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              
              {/* Account Name Section - Only for Super-Admins */}
              {isSuperAdmin && (
              <>
              <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Account Information</h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">Manage your account details.</p>
                </div>
                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                  <form onSubmit={handleAccountNameUpdate} className="space-y-4">
                    <div>
                      <label htmlFor="accountName" className="block text-sm font-medium text-gray-700">
                        Account Name
                      </label>
                      <div className="mt-1 flex rounded-md shadow-sm">
                        <input
                          type="text"
                          name="accountName"
                          id="accountName"
                          value={accountName}
                          onChange={(e) => setAccountName(e.target.value)}
                          className="flex-1 focus:ring-blue-500 focus:border-blue-500 block w-full min-w-0 rounded-md sm:text-sm border-gray-300"
                          placeholder="My Company Account"
                        />
                        <button
                          type="submit"
                          disabled={updatingAccountName || accountName === accountData?.name}
                          className="ml-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
                        >
                          {updatingAccountName ? 'Saving...' : 'Update'}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              
              <div className="mt-6 bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">Subscription Information</h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">Your current subscription details.</p>
                </div>
                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Current Plan</dt>
                      <dd className="mt-1 text-sm text-gray-900 flex items-center">
                        <span className="capitalize font-semibold">{accountData?.subscription_tier || 'Basic'}</span>
                        {accountData?.subscription_tier === 'basic' && (
                          <a href="#" className="ml-2 text-blue-600 hover:text-blue-800 text-sm">
                            Upgrade to Pro
                          </a>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Status</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          accountData?.subscription_status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {accountData?.subscription_status || 'Active'}
                        </span>
                      </dd>
                    </div>
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Features</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        <ul className="list-disc list-inside space-y-1">
                          {accountData?.subscription_tier === 'basic' ? (
                            <>
                              <li>1 Organization</li>
                              <li>1 Workspace (Default only)</li>
                              <li>Basic AI Summaries</li>
                              <li>Community Support</li>
                            </>
                          ) : accountData?.subscription_tier === 'professional' ? (
                            <>
                              <li>Advanced AI Analysis</li>
                              <li>Priority Support</li>
                              <li>Unlock Additional Organizations</li>
                              <li>Unlock Additional Workspaces</li>
                            </>
                          ) : accountData?.subscription_tier === 'enterprise' ? (
                            <>
                              <li>Everything in Pro</li>
                              <li>API Access</li>
                              <li>SSO Integration</li>
                              <li>Dedicated Support</li>
                              <li>SLA Guarantee</li>
                            </>
                          ) : null}
                        </ul>
                      </dd>
                    </div>
                  </dl>
                </div>
              </div>
              </>
              )}
              
              <div className="mt-10">
                <div className="bg-white shadow sm:rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Delete your account</h3>
                    <div className="mt-2 max-w-xl text-sm text-gray-500">
                      <p>Once you delete your account, you will lose all data associated with it.</p>
                    </div>
                    <div className="mt-5">
                      <button
                        type="button"
                        onClick={handleDeleteAccount}
                        disabled={deletingAccount}
                        className={`inline-flex items-center justify-center px-4 py-2 border border-transparent font-medium rounded-md text-red-700 ${
                          deletingAccount ? 'bg-gray-100 cursor-not-allowed' : 'bg-red-100 hover:bg-red-200'
                        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm`}
                      >
                        {deletingAccount ? 'Deleting...' : 'Delete account'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              </>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}